import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CssBaseline, Backdrop, CircularProgress, Grid, Typography, Box,
  Card, CardContent, Button, useMediaQuery, useTheme, ToggleButton, ToggleButtonGroup,
  Stepper, Step, StepLabel, Chip, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  PieChart as RechartsPieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer,
  BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, AreaChart, Area,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart,
} from 'recharts';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import {
  DashboardCountsFiltered, DashboardpieChart, MonthlyPaymentsSummary, DashboardDuepieChart,
} from '../Apihelper/Dashboard.js';

const BG = '#F4F7F4';

const buildChartColors = (appColors) => ({
  PRIMARY: appColors.leaf,
  PRIMARY_L: appColors.pine,
  ACCENT: appColors.evergreen,
  COLORS: [appColors.leaf, appColors.pine, appColors.evergreen, '#FFBB28', '#FF8042', '#8e44ad', appColors.leaf],
});

const INVOICE_TYPES = ['Donut', 'Pie', 'Bar', 'H.Bar', 'Radar', 'Area'];
const DUE_TYPES     = ['Donut', 'Pie', 'Bar', 'H.Bar', 'Radar'];
const INCOME_TYPES  = ['Bar', 'Line', 'Area', 'Composed'];

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' },   { value: 5, label: 'May' },       { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },    { value: 9, label: 'September' },
  { value: 10, label: 'October' },{ value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 8 }, (_, i) => currentYear - i);

const steps = ['Page 1', 'Page 2'];

/* ─── single-series chart renderer ─── */
function renderChart(data, type, valueKey, nameKey, chartColors) {
  const { PRIMARY, COLORS } = chartColors;
  if (!data || !data.length)
    return <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>No data available</Box>;
  const m = { top: 10, right: 20, left: 10, bottom: 10 };
  switch (type) {
    case 'Donut':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RechartsPieChart>
            <Pie data={data} dataKey={valueKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius={100} innerRadius={55} label>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
          </RechartsPieChart>
        </ResponsiveContainer>
      );
    case 'Pie':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RechartsPieChart>
            <Pie data={data} dataKey={valueKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius={110} label>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
          </RechartsPieChart>
        </ResponsiveContainer>
      );
    case 'Bar':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RechartsBarChart data={data} margin={m}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey={valueKey} name="Amount" radius={[4, 4, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      );
    case 'H.Bar':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RechartsBarChart data={data} layout="vertical" margin={{ top: 10, right: 20, left: 70, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey={nameKey} width={80} tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey={valueKey} name="Amount" radius={[0, 4, 4, 0]}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      );
    case 'Radar':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={data} cx="50%" cy="50%" outerRadius={100}>
            <PolarGrid /><PolarAngleAxis dataKey={nameKey} tick={{ fontSize: 11 }} /><PolarRadiusAxis tick={{ fontSize: 10 }} />
            <Radar name="Amount" dataKey={valueKey} stroke={PRIMARY} fill={PRIMARY} fillOpacity={0.45} />
            <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      );
    case 'Area':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={m}>
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PRIMARY} stopOpacity={0.35} />
                <stop offset="95%" stopColor={PRIMARY} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey={valueKey} name="Amount" stroke={PRIMARY} fill="url(#areaGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      );
    default: return null;
  }
}

/* ─── multi-series income chart renderer ─── */
function renderIncomeChart(data, type, chartColors) {
  const { PRIMARY, ACCENT } = chartColors;
  if (!data || !data.length)
    return <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>No data available</Box>;
  const m = { top: 10, right: 20, left: 10, bottom: 10 };
  const series = [
    { key: 'payments',    name: 'Payments (₹)',   color: PRIMARY },
    { key: 'attendance',  name: 'Attendance',      color: ACCENT },
    { key: 'new_members', name: 'New Members',     color: '#FFBB28' },
  ];
  switch (type) {
    case 'Bar':
      return (
        <ResponsiveContainer width="100%" height={320}>
          <RechartsBarChart data={data} margin={m}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      );
    case 'Line':
      return (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={m}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={{ r: 3, fill: s.color }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    case 'Area':
      return (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data} margin={m}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`grad_${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} fill={`url(#grad_${s.key})`} strokeWidth={2} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );
    case 'Composed':
    default:
      return (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data} margin={m}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 8 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="payments" name="Payments (₹)" fill={PRIMARY} opacity={0.85} radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="attendance" name="Attendance" stroke={ACCENT} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="new_members" name="New Members" stroke="#FFBB28" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      );
  }
}

/* ─── chart card with type toggle ─── */
function ChartSection({ title, subtitle, data, types, valueKey, nameKey, nested = false, multiSeries = false, rightSlot }) {
  const { colors: appColors } = useAppTheme();
  const chartColors = buildChartColors(appColors);
  const { PRIMARY, PRIMARY_L } = chartColors;
  const [chartType, setChartType] = useState(types[0]);

  const inner = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Box>
          <Typography fontWeight={700} color={PRIMARY} sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary" display="block">{subtitle}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {rightSlot}
          <ToggleButtonGroup value={chartType} exclusive onChange={(_, v) => { if (v) setChartType(v); }} size="small" sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              px: { xs: 0.8, sm: 1.2 }, py: 0.4,
              fontSize: { xs: '0.62rem', sm: '0.72rem' }, fontWeight: 600, textTransform: 'none',
              color: '#64748b', borderColor: 'rgba(27,67,50,0.2)',
              '&.Mui-selected': { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_L } },
            },
          }}>
            {types.map((t) => <ToggleButton key={t} value={t}>{t}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        {multiSeries
          ? renderIncomeChart(data, chartType, chartColors)
          : renderChart(data, chartType, valueKey, nameKey, chartColors)}
      </Box>
    </>
  );

  if (nested) return inner;
  return (
    <Card sx={{ borderRadius: '16px', mb: 2.5, boxShadow: '0 2px 16px rgba(27,67,50,0.08)', border: '1px solid rgba(27,67,50,0.08)', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>{inner}</CardContent>
    </Card>
  );
}

/* ─── hero stat card ─── */
function HeroCard({ icon, label, value, sub, onClick, dark }) {
  const { colors: appColors } = useAppTheme();
  const PRIMARY   = appColors.leaf;
  const PRIMARY_L = appColors.pine;
  const ACCENT    = appColors.evergreen;
  return (
    <Card onClick={onClick} sx={{
      borderRadius: '20px', cursor: onClick ? 'pointer' : 'default',
      background: dark ? `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_L} 100%)` : '#fff',
      boxShadow: dark ? `0 8px 24px rgba(27,67,50,0.35)` : '0 2px 12px rgba(27,67,50,0.08)',
      border: dark ? 'none' : '1px solid rgba(27,67,50,0.08)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: dark ? `0 16px 32px rgba(27,67,50,0.4)` : '0 8px 24px rgba(27,67,50,0.14)' },
      p: 0,
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ p: 1, borderRadius: '12px', backgroundColor: dark ? 'rgba(255,255,255,0.15)' : 'rgba(27,67,50,0.08)', display: 'flex' }}>
            {React.cloneElement(icon, { sx: { fontSize: 22, color: dark ? '#fff' : PRIMARY } })}
          </Box>
          <Box sx={{ p: 0.5, borderRadius: '8px', backgroundColor: dark ? 'rgba(255,255,255,0.15)' : 'rgba(82,183,136,0.12)', display: 'flex' }}>
            <NorthEastIcon sx={{ fontSize: 16, color: dark ? '#fff' : ACCENT }} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.9rem' }, fontWeight: 800, color: dark ? '#fff' : PRIMARY, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: dark ? 'rgba(255,255,255,0.75)' : '#64748b', mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        {sub && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <TrendingUpIcon sx={{ fontSize: 13, color: dark ? ACCENT : ACCENT }} />
            <Typography sx={{ fontSize: '0.72rem', color: dark ? 'rgba(255,255,255,0.8)' : ACCENT, fontWeight: 500 }}>{sub}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── small inline stat pill ─── */
function StatPill({ icon, label, value, color = PRIMARY, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minWidth: { xs: 120, sm: 140 }, flexShrink: 0,
      background: '#fff', borderRadius: '14px', p: '14px 16px',
      border: `1px solid ${color}22`,
      boxShadow: '0 2px 8px rgba(27,67,50,0.06)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s, box-shadow 0.15s',
      ...(onClick && { '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(27,67,50,0.12)' } }),
    }}>
      <Box sx={{ mb: 0.5, p: 0.7, borderRadius: '10px', backgroundColor: `${color}15`, display: 'flex' }}>
        {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
      </Box>
      <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' }, fontWeight: 800, color, lineHeight: 1.1 }}>
        {value ?? '—'}
      </Typography>
      <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', mt: 0.4, textAlign: 'center' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ─── Dashboard ─── */
const Dashboard = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { colors: appColors } = useAppTheme();
  const PRIMARY   = appColors.leaf;
  const PRIMARY_L = appColors.pine;
  const ACCENT    = appColors.evergreen;
  const [activeStep, setActiveStep] = useState(0);

  const [dashboardData, setDashboardData] = useState(null);
  const [invoiceData, setInvoiceData]     = useState([]);
  const [dueData, setDueData]             = useState([]);
  const [incomeData, setIncomeData]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [chartLoading, setChartLoading]   = useState(false);
  const [error, setError]                 = useState(null);

  /* filter state */
  const [filterType, setFilterType]   = useState('monthly');
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear]   = useState(currentYear);
  const [chartYear, setChartYear]     = useState(currentYear);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem('userdata');
    if (!raw) { navigate('/'); return; }
    try { setIsAdmin(JSON.parse(raw)?.role === 'admin'); } catch { setIsAdmin(false); }
  }, []);

  const loadCounts = useCallback(async () => {
    setLoading(true);
    try {
      const { token, id: userId } = JSON.parse(localStorage.getItem('userdata'));
      const params = { filter_type: filterType, year: filterYear };
      if (filterType === 'monthly') params.month = filterMonth;

      const [counts, pie, due] = await Promise.all([
        DashboardCountsFiltered(token, params),
        DashboardpieChart(token, userId),
        DashboardDuepieChart(token, userId),
      ]);

      if (counts.code === 200 || counts.code === '200') setDashboardData(counts.data || counts);
      else setError(counts.message || 'Failed to load dashboard');
      if (pie.code === 200 || pie.code === '200') setInvoiceData(pie.data || []);
      if (due.code === 200 || due.code === '200') setDueData(due.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filterType, filterMonth, filterYear]);

  const loadChart = useCallback(async () => {
    setChartLoading(true);
    try {
      const { token } = JSON.parse(localStorage.getItem('userdata'));
      const income = await MonthlyPaymentsSummary(token, chartYear);
      if (income.code === 200 || income.code === '200') setIncomeData(income.data || []);
    } catch (_) {}
    finally { setChartLoading(false); }
  }, [chartYear]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadChart(); }, [loadChart]);

  if (loading) return <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open><CircularProgress style={{ color: PRIMARY }} /></Backdrop>;
  if (error)   return <Typography color="error" sx={{ p: 4 }}>{error}</Typography>;

  const d = dashboardData || {};

  const periodLabel = filterType === 'monthly'
    ? `${MONTHS.find((m) => m.value === filterMonth)?.label} ${filterYear}`
    : `Year ${filterYear}`;

  // Same period definition the backend uses for period_new_members / today_new_members
  // (member's initial package start_date falling within the range) — used to drill
  // into the Member List filtered to just those joiners when a stat pill is clicked.
  const pad2 = (n) => String(n).padStart(2, '0');
  const periodJoinRange = filterType === 'monthly'
    ? {
        from: `${filterYear}-${pad2(filterMonth)}-01`,
        to: `${filterYear}-${pad2(filterMonth)}-${pad2(new Date(filterYear, filterMonth, 0).getDate())}`,
      }
    : { from: `${filterYear}-01-01`, to: `${filterYear}-12-31` };
  const todayStr = new Date().toISOString().slice(0, 10);

  const heroCards = [
    { icon: <GroupIcon />, label: 'Total Members', value: d.members, sub: 'All registered', route: '/memberList', dark: true },
    { icon: <HowToRegIcon />, label: 'Active Members', value: d.active_members, sub: 'Currently active', route: '/memberList', state: { statusFilter: 1 } },
    { icon: <AccountBalanceWalletIcon />, label: 'Total Employees', value: d.employees, sub: 'All employees', route: '/employeesdetail' },
    { icon: <ReportProblemOutlinedIcon />, label: 'Total Due', value: d.total_due != null ? `₹${d.total_due}` : '—', sub: 'Pending collection', route: '/listdueusers' },
  ];

  const breakdownState = (source, scope, label) => ({
    source,
    scope,
    filterType,
    year: filterYear,
    month: filterMonth,
    label,
    periodLabel: scope === 'today' ? `Today — ${todayStr}` : periodLabel,
  });

  const paymentStrip = [
    { title: 'Monthly Package', value: d.period_main_pay != null ? `₹${d.period_main_pay}` : '—', route: '/payment-breakdown', state: breakdownState('main', 'period', 'Monthly Package') },
    { title: 'Monthly PT', value: d.period_trainer_pay != null ? `₹${d.period_trainer_pay}` : '—', route: '/payment-breakdown', state: breakdownState('trainer', 'period', 'Monthly PT') },
    { title: 'Total Month', value: d.period_total_payments != null ? `₹${d.period_total_payments}` : '—', route: '/payment-breakdown', state: breakdownState('total', 'period', 'Total Month') },
    { title: 'Today Package', value: d.today_main_pay != null ? `₹${d.today_main_pay}` : '—', route: '/payment-breakdown', state: breakdownState('main', 'today', 'Today Package') },
    { title: 'Today PT', value: d.today_trainer_pay != null ? `₹${d.today_trainer_pay}` : '—', route: '/payment-breakdown', state: breakdownState('trainer', 'today', 'Today PT') },
    { title: 'Today Total', value: d.today_total_payments != null ? `₹${d.today_total_payments}` : '—', route: '/payment-breakdown', state: breakdownState('total', 'today', 'Today Total') },
    { title: 'SMS Balance', value: d.sms_balance ?? '—' },
  ];

  const filterSelectSx = {
    minWidth: 110, height: 36,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(27,67,50,0.25)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    fontSize: '0.82rem',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minHeight: '100vh', backgroundColor: BG }}>
      <CssBaseline />
      <Header />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden', overflowY: 'auto', p: { xs: '12px', sm: '20px', md: '24px' }, mt: { xs: '56px', sm: '64px' } }}>

        {/* ── Top bar ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: '22px', sm: '28px' }, fontWeight: 800, color: PRIMARY }}>Dashboard</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mt: 0.25 }}>Welcome back — here's what's happening today.</Typography>
          </Box>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => navigate('/newmember')} sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: '12px',
            background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_L})`,
            boxShadow: `0 4px 14px rgba(27,67,50,0.35)`, px: 2.5, py: 1.1,
            '&:hover': { background: `linear-gradient(135deg, ${PRIMARY_L}, ${PRIMARY})`, boxShadow: `0 6px 20px rgba(27,67,50,0.45)` },
          }}>
            New Joining
          </Button>
        </Box>

        {/* ── Filter bar ── */}
        <Card sx={{ borderRadius: '14px', mb: 2.5, boxShadow: '0 2px 12px rgba(27,67,50,0.07)', border: '1px solid rgba(27,67,50,0.08)' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <CalendarMonthIcon sx={{ color: PRIMARY, fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY, mr: 0.5 }}>Filter Period:</Typography>

              <ToggleButtonGroup
                value={filterType} exclusive
                onChange={(_, v) => { if (v) setFilterType(v); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                    color: '#64748b', borderColor: 'rgba(27,67,50,0.2)',
                    '&.Mui-selected': { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_L } },
                  },
                }}
              >
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="yearly">Yearly</ToggleButton>
              </ToggleButtonGroup>

              {filterType === 'monthly' && (
                <FormControl size="small">
                  <Select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} sx={filterSelectSx} displayEmpty>
                    {MONTHS.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.82rem' }}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small">
                <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} sx={filterSelectSx} displayEmpty>
                  {YEARS.map((y) => <MenuItem key={y} value={y} sx={{ fontSize: '0.82rem' }}>{y}</MenuItem>)}
                </Select>
              </FormControl>

              <Chip
                label={periodLabel}
                size="small"
                sx={{ backgroundColor: `${PRIMARY}15`, color: PRIMARY, fontWeight: 700, fontSize: '0.75rem', border: `1px solid ${PRIMARY}30` }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* ── Hero stat cards ── */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {heroCards.map((c, i) => (
            <Grid key={i} size={{ xs: 6, sm: 6, md: 3 }}>
              <HeroCard icon={c.icon} label={c.label} value={c.value} sub={c.sub} dark={c.dark} onClick={c.route ? () => navigate(c.route, { state: c.state }) : null} />
            </Grid>
          ))}
        </Grid>

        {/* ── Period stats ── */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.2 }}>
          Period Stats — {periodLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, mb: 2.5, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(27,67,50,0.2)', borderRadius: 2 } }}>
          <StatPill icon={<DirectionsRunIcon />} label="Today's Attendance" value={d.today_attendance} color={PRIMARY} />
          <StatPill
            icon={<PersonAddIcon />}
            label="Today Joined"
            value={d.today_new_members}
            color="#00897b"
            onClick={() => navigate('/memberList', { state: { joinFrom: todayStr, joinTo: todayStr, joinRangeLabel: 'Today Joined' } })}
          />
          <StatPill icon={<EventBusyIcon />}     label="Expired Members"    value={d.expired_members}   color="#e53935" />
          <StatPill icon={<FitnessCenterIcon />} label="Active PT Members"  value={d.active_pt_members} color="#7b1fa2" />
          <StatPill icon={<HowToRegIcon />}      label="Period Attendance"  value={d.period_attendance} color={ACCENT} />
          <StatPill
            icon={<PersonAddIcon />}
            label="New Joins"
            value={d.period_new_members}
            color="#3949ab"
            onClick={() => navigate('/memberList', { state: { joinFrom: periodJoinRange.from, joinTo: periodJoinRange.to, joinRangeLabel: `New Joins — ${periodLabel}` } })}
          />
          <StatPill icon={<TrendingUpIcon />}    label="New PT Signups"     value={d.period_new_pt_signups} color={PRIMARY_L} />
          <StatPill icon={<GroupIcon />}         label="Enquiries"          value={d.period_enquiries}  color="#f57c00" />
        </Box>

        {/* ── Payment quick stats strip ── */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.2 }}>
          Quick Stats
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, mb: 2.5, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(27,67,50,0.2)', borderRadius: 2 } }}>
          {paymentStrip.map((c, i) => (
            <Box
              key={i}
              onClick={c.route ? () => navigate(c.route, { state: c.state }) : undefined}
              sx={{
                minWidth: { xs: 130, sm: 150 }, flexShrink: 0,
                background: '#fff', borderRadius: '14px', p: '12px 16px',
                border: '1px solid rgba(27,67,50,0.08)',
                boxShadow: '0 2px 8px rgba(27,67,50,0.06)',
                cursor: c.route ? 'pointer' : 'default',
                transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': c.route ? { transform: 'translateY(-2px)', boxShadow: '0 4px 14px rgba(27,67,50,0.12)' } : {},
              }}
            >
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                {c.title}
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, background: `linear-gradient(90deg, ${PRIMARY}, ${PRIMARY_L})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {c.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── Invoices & Dues ── */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>
          Invoices &amp; Dues
        </Typography>
        <Card sx={{ borderRadius: '16px', mb: 2.5, boxShadow: '0 2px 16px rgba(27,67,50,0.08)', border: '1px solid rgba(27,67,50,0.08)', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, borderRight: { xs: 'none', md: '1px solid rgba(27,67,50,0.08)' }, borderBottom: { xs: '1px solid rgba(27,67,50,0.08)', md: 'none' } }}>
              <ChartSection title="Total Invoice (by category)" data={invoiceData} types={INVOICE_TYPES} valueKey="value" nameKey="name" nested />
            </Box>
            <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 } }}>
              <ChartSection title="Due Amount (by category)" data={dueData} types={DUE_TYPES} valueKey="total" nameKey="name" nested />
            </Box>
          </Box>
        </Card>

        {/* ── Monthly Payments Summary (multi-series) ── */}
        {isAdmin && (
        <>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>
          Monthly Payments Summary
        </Typography>
        <ChartSection
          title="Payments · Attendance · New Members"
          subtitle="Monthly breakdown across all categories"
          data={incomeData}
          types={INCOME_TYPES}
          multiSeries
          rightSlot={
            <FormControl size="small">
              <Select
                value={chartYear}
                onChange={(e) => setChartYear(e.target.value)}
                sx={{ ...filterSelectSx, minWidth: 90 }}
                displayEmpty
              >
                {YEARS.map((y) => <MenuItem key={y} value={y} sx={{ fontSize: '0.82rem' }}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          }
        />
        </>
        )}

        {/* ── Stepper + navigation ── */}
        <Box sx={{ mt: 4, overflowX: 'auto' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ minWidth: { xs: 260, sm: 320 } }}>
            {steps.map((label, i) => (
              <Step key={i}>
                <StepLabel sx={{
                  '& .MuiStepLabel-label': { fontSize: { xs: '0.7rem', sm: '0.875rem' } },
                  '& .MuiStepIcon-root.Mui-active': { color: PRIMARY },
                  '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
                }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', mt: 2.5, mb: 3 }}>
          <Button
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            disabled={activeStep === 0} fullWidth={isMobile}
            sx={{ color: PRIMARY, fontWeight: 600, textTransform: 'none', borderRadius: '12px', px: 3, py: 1.2, border: `1px solid rgba(27,67,50,0.2)`, '&:hover': { backgroundColor: 'rgba(27,67,50,0.05)' } }}
          >
            Back
          </Button>
          <Button
            variant="contained" onClick={() => navigate('/dashboard-next')} fullWidth={isMobile}
            sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '12px', px: 3, py: 1.2, background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_L})`, boxShadow: `0 4px 14px rgba(27,67,50,0.3)`, '&:hover': { background: `linear-gradient(135deg, ${PRIMARY_L}, ${PRIMARY})`, boxShadow: `0 6px 20px rgba(27,67,50,0.4)`, transform: 'translateY(-1px)' } }}
          >
            Go to Next Page
          </Button>
        </Box>

      </Box>
    </Box>
  );
};

export default Dashboard;
